/**
 * Dashboard — logged-in home.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronRight, Mail, ShieldCheck, TrendingUp, PlayCircle } from "lucide-react";
import { toast } from "sonner";

import AppShell from "../components/ui-r1/AppShell";
import LiveActivityTicker from "../components/ui-r1/LiveActivityTicker";
import WalletChip from "../components/ui-r1/WalletChip";
import { FeaturedGameTile, ComingSoonTile } from "../components/ui-r1/GameTile";
import PromoCard from "../components/ui-r1/PromoCard";
import BonusPromoStrip from "../components/BonusPromoStrip";
import { Badge } from "../components/ui-r1/Badge";
import Button from "../components/ui-r1/Button";
import Input from "../components/ui-r1/Input";
import ChatWidget from "../components/ChatWidget";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { getBalance } from "../lib/currency";
import { extractError } from "../lib/formatError";
import api from "../lib/api";

const HEROS = [
  { image: "/assets/brand/hero_1_welcome.png", title: "First deposit 100% up to $500", subtitle: "Automatic on first qualifying deposit. No wagering." },
  { image: "/assets/brand/hero_2_bra_3way.png", title: "Bra 3-Way · 2.85× on winning color", subtitle: "Three colors. One dress. Provably fair.", cta: "Play", href: "/game" },
  { image: "/assets/brand/hero_3_vip.png", title: "VIP · Cashback & personal host", subtitle: "Deposit $500 lifetime to qualify.", cta: "See VIP", href: "/vip" },
];

const COMING = [
  { id: "roulette",   title: "Bra Roulette",    image: "/assets/brand/coming_bra_roulette.png" },
  { id: "slots",      title: "Bra Slots",       image: "/assets/brand/coming_bra_slots.png" },
  { id: "blackjack",  title: "Bra Blackjack",   image: "/assets/brand/coming_bra_blackjack.png" },
];

const SEXY_TATTOO_HERO = "/api/assets/models/sexy-tattoo/model_01/covered.png";
const HOT_BIKINI_HERO = "/api/assets/models/hot-bikini/model_01/dressed.png";
const SCRATCH_CARD_HERO = "/api/assets/models/scratch-card/model_01/dressed.png";

function isWithin7Days(iso) {
  if (!iso) return false;
  const t = new Date(iso).getTime();
  if (isNaN(t)) return false;
  return Date.now() - t < 7 * 24 * 3600 * 1000;
}

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const { activeCurrency } = useCurrency();
  const nav = useNavigate();
  const [recent, setRecent] = useState([]);
  const [playersOnline] = useState(() => 220 + Math.floor(Math.random() * 200));
  // Registry-driven per-game flags — currently only `is_new` for the
  // NEW-badge overlay. Fetched once on mount; refreshes when the
  // admin flips the toggle on their next reload cycle (a stale flag
  // is fine — the badge is purely marketing, not gameplay-critical).
  const [gamesBySlug, setGamesBySlug] = useState({});

  useEffect(() => {
    api.get("/games")
      .then(({ data }) => {
        const map = {};
        for (const g of (data.games || [])) map[g.slug] = g;
        setGamesBySlug(map);
      })
      .catch(() => setGamesBySlug({}));
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/game/history?limit=5").then(({ data }) => setRecent(data.items || [])).catch(() => setRecent([]));
    // Only refetch when the user *identity* changes (login/logout). Depending
    // on the full `user` object would refetch on every profile refresh
    // (balance updates, KYC status change, etc.), which is wasteful here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  if (!user) return null;

  // Prefer username (Phase-2) over the local-part of email for the greeting —
  // username-first accounts may not have an email at all.
  const displayName = user.username || (user.email ? user.email.split("@")[0] : "player");
  const realActive = getBalance(user, "real", activeCurrency);
  const demoActive = getBalance(user, "demo", activeCurrency);

  return (
    <AppShell activeSection="casino" pageTitle="Dashboard">
      <LiveActivityTicker />

      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 py-6 space-y-8">
        {/* Welcome + wallet cards */}
        <section data-testid="dashboard-welcome">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-3 mb-4">
            <div>
              <div className="text-[10px] tracking-[0.4em] uppercase text-pink">Welcome back</div>
              <h1 className="font-display italic text-3xl sm:text-4xl text-ink" data-testid="dashboard-hello">{displayName}</h1>
              <div className="flex items-center gap-2 mt-2">
                <Badge variant={user.kyc_status === "approved" ? "success" : "muted"}>KYC · {user.kyc_status || "none"}</Badge>
                <Badge variant="muted">{user.country_code || "—"}</Badge>
                <Badge variant="muted">{activeCurrency}</Badge>
              </div>
            </div>
            <div className="flex gap-2">
              <Link to="/game/bra-3-way"><Button variant="primary" size="lg"><PlayCircle className="w-4 h-4" />Play Bra 3-Way</Button></Link>
              <Link to={`/wallet/deposit?currency=${activeCurrency}`}><Button variant="gold" size="lg">Deposit</Button></Link>
            </div>
          </div>
          <WalletChip size="lg" real={realActive} demo={demoActive} currency={activeCurrency} />
        </section>

        {/* Add / verify email card — Phase-2 username-first accounts can attach
            an email later. Shows: (a) an "Add email" CTA if none set, or
            (b) an unverified banner if one is set but not confirmed. */}
        <EmailStatusCard user={user} onSaved={refresh} />

        {/* Welcome bonus received card — shows for 7 days after claim */}
        {user.bonus_claimed && isWithin7Days(user.bonus_claimed_at) && (
          <section data-testid="dashboard-bonus-received">
            <div className="rounded-xl border border-gold/50 bg-gradient-to-br from-gold/15 to-pink/5 p-5 flex items-center gap-4 shadow-glow-gold">
              <span className="w-12 h-12 rounded-xl bg-gold-gradient flex items-center justify-center text-[#0B0E1E] text-xl">🎁</span>
              <div className="flex-1">
                <div className="text-[10px] tracking-[0.35em] uppercase text-gold mb-1">Welcome bonus received</div>
                <div className="font-display italic text-2xl gold-text">+${(user.bonus_amount_usd || 0).toFixed(2)} credited</div>
                <div className="text-xs text-ink-2 mt-1">
                  {user.bonus_claimed_at ? `Received ${new Date(user.bonus_claimed_at).toLocaleDateString()}` : "Recently received"}
                  {" · No wagering. Yours to play or withdraw after KYC."}
                </div>
              </div>
              <Link to="/promotions#welcome" className="hidden sm:block">
                <Button variant="ghost" size="sm">Details</Button>
              </Link>
            </div>
          </section>
        )}

        {/* Featured + coming soon */}
        <BonusPromoStrip limit={3} testid="dashboard-promo-strip" />

        <section data-testid="dashboard-games">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-[10px] tracking-[0.4em] uppercase text-pink mb-1">Casino</div>
              <h2 className="font-display italic text-2xl sm:text-3xl text-ink">Featured</h2>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <FeaturedGameTile
              image="/assets/brand/hero_2_bra_3way.png"
              title="Bra 3-Way"
              subtitle="Provably-fair · 2.85× payout"
              playersOnline={playersOnline}
              onPlay={() => nav("/game/bra-3-way")}
              testid="dashboard-featured-3way-tile"
              isNew={!!gamesBySlug["bra-3-way"]?.is_new}
            />
            <FeaturedGameTile
              image="/assets/brand/hero_2_bra_3way.png"
              title="Bra 2-Way"
              subtitle="2 colors · 1.85× payout · faster rounds"
              playersOnline={Math.max(0, playersOnline - 90)}
              onPlay={() => nav("/game/bra-2-way")}
              testid="dashboard-featured-2way-tile"
              isNew={!!gamesBySlug["bra-2-way"]?.is_new}
            />
            <FeaturedGameTile
              image={SEXY_TATTOO_HERO}
              title="Sexy Tattoo"
              subtitle="Tattoo or no tattoo · 1.85× · new drop"
              playersOnline={Math.max(0, playersOnline - 130)}
              onPlay={() => nav("/game/sexy-tattoo")}
              testid="dashboard-featured-tattoo-tile"
              accent="pink"
              isNew={!!gamesBySlug["sexy-tattoo"]?.is_new}
            />
            <FeaturedGameTile
              image={HOT_BIKINI_HERO}
              title="Hot Bikini"
              subtitle="Red or black · 1.85× · fresh drop"
              playersOnline={Math.max(0, playersOnline - 110)}
              onPlay={() => nav("/game/hot-bikini")}
              testid="dashboard-featured-bikini-tile"
              accent="pink"
              isNew={!!gamesBySlug["hot-bikini"]?.is_new}
            />
            <FeaturedGameTile
              image={SCRATCH_CARD_HERO}
              title="Scratch Card"
              subtitle="Buy · scratch · win up to 200× · fresh drop"
              playersOnline={Math.max(0, playersOnline - 150)}
              onPlay={() => nav("/game/scratch-card")}
              testid="dashboard-featured-scratch-tile"
              accent="pink"
              isNew={!!gamesBySlug["scratch-card"]?.is_new}
            />
          </div>
          <div className="mt-4 grid grid-cols-3 gap-3">
            {COMING.slice(0, 3).map((c) => <ComingSoonTile key={c.id} image={c.image} title={c.title} testid={`dashboard-coming-${c.id}`} />)}
          </div>
        </section>

        {/* Your Bonuses */}
        <section data-testid="dashboard-bonuses">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-[10px] tracking-[0.4em] uppercase text-pink mb-1">Your rewards</div>
              <h2 className="font-display italic text-2xl sm:text-3xl text-ink">Bonuses</h2>
            </div>
            <Link to="/promotions" className="text-[10px] tracking-widest uppercase text-ink-2 hover:text-pink">All <ChevronRight className="w-3 h-3 inline" /></Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <BonusCard title="Weekly cashback" desc="5% of net losses returned every Monday" progress={35} testid="bonus-cashback" />
            <BonusCard title="Refer a friend" desc="Give $50, get $50 when they deposit $100+" progress={0} testid="bonus-refer" />
          </div>
        </section>

        {/* Recent activity */}
        <section data-testid="dashboard-activity">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="text-[10px] tracking-[0.4em] uppercase text-pink mb-1">Activity</div>
              <h2 className="font-display italic text-2xl sm:text-3xl text-ink">Recent rounds</h2>
            </div>
            <Link to="/history" className="text-[10px] tracking-widest uppercase text-ink-2 hover:text-pink">Full history <ChevronRight className="w-3 h-3 inline" /></Link>
          </div>
          {recent.length === 0 ? (
            <div className="rounded-xl border border-line bg-elevated p-8 text-center text-ink-2 text-sm">
              No rounds yet. <Link to="/game/bra-3-way" className="text-pink hover:underline">Play your first</Link> to see history here.
            </div>
          ) : (
            <div className="rounded-xl border border-line bg-elevated overflow-hidden">
              <table className="w-full text-sm">
                <thead className="border-b border-line text-[10px] tracking-widest uppercase text-ink-3">
                  <tr>
                    <Th>Date</Th><Th>Bet</Th><Th>Pick</Th><Th>Result</Th><Th className="text-right">Payout</Th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((r) => (
                    <tr key={r.round_id} className="border-b border-line last:border-0" data-testid={`activity-${r.round_id}`}>
                      <Td className="text-ink-3 text-xs">{new Date(r.created_at).toLocaleString()}</Td>
                      <Td className="font-mono text-ink">${r.bet_amount.toFixed(2)}</Td>
                      <Td><ColorChip color={r.color_pick} /></Td>
                      <Td>{r.won ? <Badge variant="success">WIN</Badge> : <Badge variant="danger">LOSS</Badge>}</Td>
                      <Td className={"text-right font-mono " + (r.won ? "text-success" : "text-ink-3")}>
                        {r.won ? `+$${r.payout.toFixed(2)}` : "—"}
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <ChatWidget />
    </AppShell>
  );
}

function Th({ children, className = "" }) { return <th className={`text-left px-4 py-3 font-normal ${className}`}>{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-4 py-3 ${className}`}>{children}</td>; }

function ColorChip({ color }) {
  const map = { red: "#c73030", black: "#141110", gold: "#d4a13a" };
  return (
    <span className="inline-flex items-center gap-2">
      <span className="w-3 h-3 rounded-full border border-line" style={{ background: map[color] || "#888" }} />
      <span className="uppercase tracking-widest text-xs">{color}</span>
    </span>
  );
}

function BonusCard({ title, desc, progress, testid }) {
  return (
    <div className="rounded-xl border border-line bg-elevated p-5" data-testid={testid}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="font-display italic text-lg text-ink mb-1">{title}</div>
          <div className="text-xs text-ink-2">{desc}</div>
        </div>
        <TrendingUp className="w-4 h-4 text-gold shrink-0" />
      </div>
      <div className="h-1.5 rounded-full bg-highest overflow-hidden">
        <div className="h-full bg-brand-gradient" style={{ width: `${progress}%` }} />
      </div>
      <div className="text-[10px] tracking-widest uppercase text-ink-3 mt-2">{progress}% to next payout</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Email add / verify card — Phase-2 username-first accounts can attach an
// email later via `POST /auth/add-email`. If the user already has an email
// but hasn't confirmed it, we surface an inline "verification pending" nudge.
// ---------------------------------------------------------------------------

const EMAIL_RE = /^[A-Za-z0-9._%+\-]+@[A-Za-z0-9.\-]+\.[A-Za-z]{2,}$/;

function EmailStatusCard({ user, onSaved }) {
  const [showAdd, setShowAdd] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Nothing to render if the user has a verified email.
  if (user?.email && user?.email_verified) return null;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!EMAIL_RE.test(email.trim())) {
      setError("Enter a valid email address.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/add-email", { email: email.trim() });
      toast.success("Verification link sent — check your inbox.");
      setShowAdd(false);
      setEmail("");
      await onSaved?.();
    } catch (e2) {
      setError(extractError(e2));
    } finally {
      setBusy(false);
    }
  };

  // Case A — user has an email but hasn't verified it.
  if (user?.email && !user?.email_verified) {
    return (
      <section data-testid="dashboard-email-verify">
        <div className="rounded-xl border border-gold/50 bg-gold/5 p-5 flex items-center gap-4">
          <span className="w-10 h-10 rounded-lg bg-gold/15 border border-gold/40 flex items-center justify-center shrink-0">
            <Mail className="w-5 h-5 text-gold" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] tracking-[0.35em] uppercase text-gold mb-1">
              Verify your email
            </div>
            <div className="text-sm text-ink truncate">
              We sent a link to{" "}
              <span className="font-mono text-ink-2">{user.email}</span>. Confirm it before your
              next withdrawal.
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Case B — no email yet. Prompt to attach one.
  return (
    <section data-testid="dashboard-email-add">
      <div className="rounded-xl border border-pink/40 bg-pink/5 p-5">
        <div className="flex items-start gap-4">
          <span className="w-10 h-10 rounded-lg bg-pink/15 border border-pink/40 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-5 h-5 text-pink" />
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] tracking-[0.35em] uppercase text-pink mb-1">
              Add an email (optional)
            </div>
            <div className="text-sm text-ink mb-3">
              Attach an email for password recovery and important account alerts. Required before
              your first real-money withdrawal if you plan to use email 2FA.
            </div>
            {!showAdd ? (
              <Button
                variant="primary"
                size="md"
                onClick={() => setShowAdd(true)}
                data-testid="dashboard-add-email-btn"
              >
                <Mail className="w-3.5 h-3.5" />
                Add email
              </Button>
            ) : (
              <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 items-start max-w-md">
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="dashboard-add-email-input"
                  autoComplete="email"
                  required
                  className="w-full"
                />
                <div className="flex gap-2 shrink-0">
                  <Button
                    type="submit"
                    variant="primary"
                    size="md"
                    loading={busy}
                    disabled={busy}
                    data-testid="dashboard-add-email-submit"
                  >
                    {busy ? "Saving…" : "Save"}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="md"
                    onClick={() => { setShowAdd(false); setError(null); setEmail(""); }}
                    data-testid="dashboard-add-email-cancel"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            )}
            {error && (
              <div
                className="mt-2 text-xs text-danger border border-danger/60 bg-danger/10 rounded-lg px-3 py-2"
                data-testid="dashboard-add-email-error"
              >
                {error}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
